import { memo } from 'react'
import { Group, Text, Avatar, MultiSelect, Select, UnstyledButton, Loader } from '@mantine/core'
import { IconCheck, IconX } from '@tabler/icons-react'
import { useArcTranslation } from 'hooks'
import type { StudentRequest, GroupOption, GradeOption } from 'api/arcMosaico/types'
import type { RowState } from './useStudentRequests'

interface Props {
  request: StudentRequest
  rowState: RowState
  groups: GroupOption[]
  grades: GradeOption[]
  /** Null when idle, 'accepting' or 'declining' when an operation is in progress */
  loading: 'accepting' | 'declining' | null
  /** True when a bulk operation (accept all) is running -- disables individual actions */
  disabled: boolean
  isLast: boolean
  onGroupChange: (groupIds: string[]) => void
  onGradeChange: (gradeId: string | null) => void
  onAccept: () => void
  onDecline: () => void
  onCreateGroup: () => void
}

function getInitials(firstName: string, lastName: string): string {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
}

export const StudentRequestRow = memo(function StudentRequestRow({
  request,
  rowState,
  groups,
  grades,
  loading,
  disabled,
  isLast,
  onGroupChange,
  onGradeChange,
  onAccept,
  onDecline,
  onCreateGroup,
}: Props) {
  const { t } = useArcTranslation()
  const isBusy = !!loading || disabled

  const groupData = [
    { value: '__create__', label: `+ ${t('student_requests.create_group')}` },
    ...groups
      .map(g => ({ value: g.id, label: g.name }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' })),
  ]

  const gradeData = grades
    .map(g => ({ value: g.id, label: g.name }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' }))

  const handleGroupChange = (values: string[]) => {
    if (values.includes('__create__')) {
      onCreateGroup()
      return
    }
    onGroupChange(values)
  }

  return (
    <Group noWrap spacing='sm' px='md' py='sm' sx={theme => ({
      borderBottom: isLast ? 'none' : `1px solid ${theme.colors.gray[1]}`,
      opacity: isBusy ? 0.6 : 1,
      transition: 'opacity 150ms',
    })}>
      <Group noWrap spacing='xs' sx={{ flex: '1 1 25%', minWidth: 0 }}>
        <Avatar size={32} radius='xl' color='gray'>
          {getInitials(request.firstName, request.lastName)}
        </Avatar>
        <Text size='sm' weight={500} truncate>
          {request.firstName} {request.lastName}
        </Text>
      </Group>

      <div style={{ flex: '1 1 30%', minWidth: 0 }}>
        <MultiSelect
          data={groupData}
          value={rowState.groupIds}
          onChange={handleGroupChange}
          placeholder={t('student_requests.select_group')}
          size='sm'
          error={rowState.error.group ? t('student_requests.required_field') : undefined}
          withinPortal
          searchable
          disabled={isBusy}
        />
      </div>

      <div style={{ flex: '1 1 20%', minWidth: 0 }}>
        <Select
          data={gradeData}
          value={rowState.gradeId}
          onChange={onGradeChange}
          placeholder={t('student_requests.select_grade')}
          size='sm'
          error={rowState.error.grade ? t('student_requests.required_field') : undefined}
          withinPortal
          disabled={isBusy}
        />
      </div>

      <Group noWrap spacing={8} sx={{ flex: '0 0 25%', minWidth: 0, justifyContent: 'flex-end' }}>
        <UnstyledButton
          onClick={onAccept}
          disabled={isBusy}
          sx={{
            backgroundColor: isBusy ? '#f1f3f5' : '#ebfbee',
            borderRadius: 20,
            padding: '4px 12px',
            '&:hover': { backgroundColor: isBusy ? '#f1f3f5' : '#d3f9d8' },
          }}
        >
          <Group noWrap spacing={4}>
            {loading === 'accepting' ? (
              <Loader size={14} color='teal' />
            ) : (
              <IconCheck size={14} color={isBusy ? '#adb5bd' : '#2B8A3E'} />
            )}
            <Text size='xs' color={isBusy ? 'dimmed' : 'teal.7'} weight={500}>
              {t('student_requests.accept')}
            </Text>
          </Group>
        </UnstyledButton>
        <UnstyledButton
          onClick={onDecline}
          disabled={isBusy}
          sx={{
            backgroundColor: isBusy ? '#f1f3f5' : '#fff5f5',
            borderRadius: 20,
            padding: '4px 12px',
            '&:hover': { backgroundColor: isBusy ? '#f1f3f5' : '#ffe3e3' },
          }}
        >
          <Group noWrap spacing={4}>
            {loading === 'declining' ? (
              <Loader size={14} color='red' />
            ) : (
              <IconX size={14} color={isBusy ? '#adb5bd' : '#E03131'} />
            )}
            <Text size='xs' color={isBusy ? 'dimmed' : 'red.7'} weight={500}>
              {t('student_requests.decline')}
            </Text>
          </Group>
        </UnstyledButton>
      </Group>
    </Group>
  )
})
